import { AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { StoreService } from 'src/app/services/store.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { decompress } from 'lz-string';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, startWith, Subject, take, takeUntil, tap } from 'rxjs';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Users } from 'src/app/Interfaces/users';
import { UtilService } from 'src/app/services/util.service';
import { Time } from 'src/app/Utility/constants';
import { AgentPerDay, AgentPerDayWithSingleDay, TeudotHistoryResponse, TotalAgentPerDay, VisitsPlannedVsUnplanned } from 'src/app/Interfaces/SQL-reports-responses';
import { environment } from 'src/environments/environment';
import { LoadingService } from 'src/app/services/loading.service';
import { VisitsService } from 'src/app/services/visits.service';
import { formatNumberToMaxPrecision, normalizeNumberToFormattedString, sumOfKeyInData } from 'src/app/Utility/functions';
import { translateDateForSQLServer, translateDateForWebISO } from 'src/app/Utility/date_functions/SQL_ISO_adapter';
import { ColumnComponent, ColumnDefinition, MenuObject, MenuSeparator } from 'tabulator-tables';
import { DohotBizoimSalesService } from 'src/app/services/dohot-bizoim-sales.service';

type TeudotHistoryResponseWithClientId = TeudotHistoryResponse & { ClientId?: string; ISODate?: string };

const headerMenu: () => MenuObject<ColumnComponent>[] = function () {
	var menu: MenuObject<ColumnComponent>[] = [];
	var columns: ColumnComponent[] = this.getColumns();

	for (let column of columns) {
		//create checkbox element using font awesome icons
		const checkbox = document.createElement('input');
		checkbox.type = 'checkbox';
		checkbox.checked = column.isVisible();

		const [label, title] = [document.createElement('span'), document.createElement('span')];

		title.textContent = ' ' + column.getDefinition().title;

		label.appendChild(checkbox);
		label.appendChild(title);

		//create menu item
		menu.push({
			label: label,
			action: function (e) {
				e.stopPropagation();
				column.toggle();
				checkbox.checked = column.isVisible();
				if (!columns.some(col => col.isVisible())) {
					column.show();
					checkbox.checked = true;
				}
			},
		});
	}

	return menu;
};

@Component({
	selector: 'app-dohot-bizoim-sales',
	templateUrl: './dohot-bizoim-sales.component.html',
	styleUrls: ['./dohot-bizoim-sales.component.scss'],
})
export class DohotBizoimSalesComponent implements OnInit, OnDestroy {
	_ss = inject(StoreService);
	router = inject(Router);
	_util = inject(UtilService);
	_visits = inject(VisitsService);
	_dohotBizoimSales = inject(DohotBizoimSalesService);
	private fb = inject(FormBuilder);
	private acr = inject(ActivatedRoute);
	private _loading = inject(LoadingService);

	constructor() {}

	AgentPerDate: AgentPerDay[];
	AgentPerDateOne: AgentPerDayWithSingleDay[];
	AdminSeeAgents: TotalAgentPerDay[];
	AdminSeeAgentsMain: TotalAgentPerDay[];

	agents = new FormControl<{ AgentID: string; UserName: string }[]>([]);
	companyControl = new FormControl<number>(0);

	form = this._dohotBizoimSales.form;
	TodayDate = new Date();

	shouldShowCompanies = 0;

	agentsToGet = [];

	datesFilter = this._dohotBizoimSales.datesFilter;

	dateFilterFunction = (date: Date): boolean => {
		const dates = this.datesFilter;

		return date ? dates.includes(date.toISOString()) : false;
	};

	isAdminPage = this.router.url.includes('admin');
	isAgentPage = this.router.url.includes('agent');

	destroy$ = new Subject<void>();

	title$ = of('ביצועים מכירות');
	showSearch$: Observable<boolean>;

	adminData: TeudotHistoryResponseWithClientId[] = this._dohotBizoimSales.adminData;
	adminData$ = this._dohotBizoimSales.adminData$;

	agentData: TeudotHistoryResponseWithClientId[] = this._dohotBizoimSales.agentData;
	agentData$ = this._dohotBizoimSales.agentData$;

	dataDisplay$ = this._dohotBizoimSales.dataDisplay$;

	totalSum = 0;

	public AllClients = [];

	public AllClientsSearch: TeudotHistoryResponseWithClientId[];

	tableHeight = '50vh';

	tabulatorColumns: (Partial<ColumnDefinition> & { title: string; field: keyof TeudotHistoryResponse })[] = [
		{
			title: 'תאריך',
			field: 'DocCreateDate',
			sorter: function (a: string, b: string) {
				if (!a || !b) {
					return 0;
				}

				const dateA = new Date(a.split('-').reverse().join('-')).getTime();
				const dateB = new Date(b.split('-').reverse().join('-')).getTime();

				return dateA - dateB;
			},
			headerMenu,
		},
		{
			title: 'סוכן',
			field: 'AgentId',
			headerMenu,
		},
		{
			title: 'סכום כולל',
			field: 'DocTotalSum',
			formatter(cell, formatterParams, onRendered) {
				// return formatNumberToMaxPrecision(cell.getValue(), 2);
				return normalizeNumberToFormattedString(cell.getValue(), { useGrouping: true });
			},
			headerMenu,
		},
		{ title: 'חברה', field: 'Company', headerMenu },
	];

	ngOnInit(): void {
		this._loading.changeLoadingStatuses({ isLoading: true });

		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////
		if (this._ss.CustomerInformation === null) {
			if (localStorage.getItem('CustomerInformation') !== null) {
				this._ss.CustomerInformation = decompress(localStorage.getItem('CustomerInformation'));
				this._ss.CustomerInformation = JSON.parse(this._ss.CustomerInformation);
			}
		}

		const username = localStorage.getItem('UserName');
		const agentid = localStorage.getItem('AgentId');

		if (!username || !agentid) {
			this.router.navigate(['login']);
			return;
		}

		this._ss.UserNameConnected = localStorage.getItem('UserName');
		this._ss.AgentIdConnected = localStorage.getItem('AgentId');

		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////

		this.setupTabularDataListener();

		if (this.isAgentPage) {
			this.tabulatorColumns = this.tabulatorColumns.filter(col => col.field !== 'AgentId');
			this.getAllAgentTeudot();
			return;
		}

		if (this._ss.agentsOfSubmanager?.length) {
			this.agentsToGet = this._ss.agentsOfSubmanager;
			this.getAllAdminTeudot();
			return;
		}

		if (this._ss.CustomerInformation.Submanagers !== '1') {
			this.getAllAdminTeudot();
			return;
		}

		this._ss
			.getAdminAgents()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res => {
					if (typeof res == 'string') {
						alert('חלה תקלה בהבאת סוכנים עבור מנהל זה. נסו שנית');
						return;
					}

					this.agentsToGet = res.recordset.map(obj => obj.Agent);
					this.agentsToGet.push(this._ss.AgentIdConnected);

					this.getAllAdminTeudot();
				},
				error: err => {
					alert('שגיאת שרת');
				},
			});
	}

	ngOnDestroy(): void {
		this._loading.changeLoadingStatuses({ isLoading: false });
		this.destroy$.next();
		this.destroy$.complete();
	}

	public BackToAdmin() {
		history.back();
	}

	async getAgentsList() {
		return await new Promise<Users[]>((resolve, rej) => {
			this._ss
				.GetAllAgents()
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: res => {
						if (typeof res === 'object' && res.recordset) {
							res.recordset.sort((a, b) => +a.AgentID - +b.AgentID);
							return resolve(res.recordset);
						}

						rej();
					},
					error: err => {
						console.log({ err });
						rej();
					},
				});
		});
	}

	resetForm() {
		this.onScrolledToRow();
		this.form.reset();
		this.adminData = this.adminData$.value.concat();
	}

	async getAllAdminTeudot() {
		// if (this.adminData.length) {

		if (this._dohotBizoimSales.adminData.length) {
			this.adminData = this.adminData$.value.concat();
			this.adminData$.next(this.adminData$.value.concat());
			if (!this._dohotBizoimSales.HasMultipleCompanies) {
				this.tabulatorColumns = this.tabulatorColumns.filter(col => col.field !== 'Company');
			}

			this._loading.changeLoadingStatuses({ isLoading: false });
			setTimeout(() => {
				this.calculateTableHeight();
			}, 0);
			return;
		}
		this._dohotBizoimSales
			.getAdminTeudot(this._ss.AdminCompanyChosen, this.agentsToGet)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res => {
					if (typeof res === 'string' || !res.recordset.length) {
						// this.Loading = false;
						alert('ישנה תקלה בהבאת נתונים. נסו שנית מאוחר יותר');
						history.back();
						return;
					}

					this._dohotBizoimSales.originalAdminData = res.recordset;

					let HasMultipleCompanies = false,
						hasToday = false,
						biggestDate = 0,
						today = new Date(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()),
						todayISO = today.toISOString().split('T')[0];

					const dates = new Set<string>();

					const data = Object.values(
						res.recordset.reduce((acc, curr) => {
							if (!curr.DocCreateDate) return acc;
							const isoDate = translateDateForWebISO(curr.DocCreateDate.split('-').reverse().join('-'));
							acc[isoDate + curr.AgentId] ||= { ...curr, DocTotalSum: 0, ISODate: isoDate.toISOString() };

							hasToday ||= isoDate.toISOString().split('T')[0] === todayISO;
							biggestDate = Math.max(biggestDate, isoDate.getTime());

							dates.add(isoDate.toISOString());

							HasMultipleCompanies ||= acc[isoDate + curr.AgentId].Company !== curr.Company;
							acc[isoDate + curr.AgentId].DocTotalSum += curr.DocTotalSum;
							return acc;
						}, {} as Record<string, TeudotHistoryResponseWithClientId>),
					);

					this._dohotBizoimSales.HasMultipleCompanies = HasMultipleCompanies;

					if (!HasMultipleCompanies) {
						this.tabulatorColumns = this.tabulatorColumns.filter(col => col.field !== 'Company');
					}

					console.log(data);

					this._dohotBizoimSales.adminData = data;

					this.adminData$.next(data);

					this.datesFilter = this._dohotBizoimSales.datesFilter = Array.from(dates);

					const dateToPatch = hasToday ? today : biggestDate;

					this.form.patchValue({
						from: new Date(dateToPatch),
						to: new Date(dateToPatch),
					});

					setTimeout(() => {
						this.calculateTableHeight();
					}, 0);

					this._loading.changeLoadingStatuses({ isLoading: false });
				},
				error: err => {
					// this.Loading = false;
					alert('ישנה תקלה בהבאת נתונים. נסו שנית מאוחר יותר');
					history.back();
				},
			});
	}
	async getAllAgentTeudot() {
		if (this.agentData.length) {
			if (!this._dohotBizoimSales.HasMultipleCompanies) {
				this.tabulatorColumns = this.tabulatorColumns.filter(col => col.field !== 'Company');
			}
			this._loading.changeLoadingStatuses({ isLoading: false });
			setTimeout(() => {
				this.calculateTableHeight();
			}, 0);
			return;
		}
		this._dohotBizoimSales
			.getAgentTeudot()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res: any) => {
					if (typeof res === 'string' || !res.recordset.length) {
						// this.Loading = false;
						alert('ישנה תקלה בהבאת נתונים. נסו שנית מאוחר יותר');
						history.back();
						return;
					}

					this._dohotBizoimSales.originalAgentData = res.recordset;

					let HasMultipleCompanies = false,
						hasToday = false,
						biggestDate = 0,
						today = new Date(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()),
						todayISO = today.toISOString().split('T')[0];

					const dates = new Set<string>();

					const data = Object.values(
						res.recordset.reduce((acc, curr) => {
							if (!curr.DocCreateDate) return acc;
							const isoDate = translateDateForWebISO(curr.DocCreateDate.split('-').reverse().join('-'));
							acc[isoDate + curr.AgentId] ||= { ...curr, DocTotalSum: 0, ISODate: isoDate.toISOString() };

							hasToday ||= isoDate.toISOString().split('T')[0] === todayISO;
							biggestDate = Math.max(biggestDate, isoDate.getTime());

							dates.add(isoDate.toISOString());

							HasMultipleCompanies ||= acc[isoDate + curr.AgentId].Company !== curr.Company;
							acc[isoDate + curr.AgentId].DocTotalSum += curr.DocTotalSum;
							return acc;
						}, {} as Record<string, TeudotHistoryResponseWithClientId>),
					);

					this._dohotBizoimSales.HasMultipleCompanies = HasMultipleCompanies;

					if (!HasMultipleCompanies) {
						this.tabulatorColumns = this.tabulatorColumns.filter(col => col.field !== 'Company');
					}

					// console.log(data);

					this._dohotBizoimSales.agentData = data as TeudotHistoryResponseWithClientId[];

					this.agentData$.next(data as any[]);

					this.datesFilter = this._dohotBizoimSales.datesFilter = Array.from(dates);

					const dateToPatch = hasToday ? today : biggestDate;

					this.form.patchValue({
						from: new Date(dateToPatch),
						to: new Date(dateToPatch),
					});

					setTimeout(() => {
						this.calculateTableHeight();
					}, 0);

					this._loading.changeLoadingStatuses({ isLoading: false });
				},
				error: err => {
					// this.Loading = false;
					alert('ישנה תקלה בהבאת נתונים. נסו שנית מאוחר יותר');
					history.back();
				},
			});
	}

	setupTabularDataListener() {
		this.dataDisplay$.pipe(takeUntil(this.destroy$)).subscribe(data => {
			this.totalSum = 0;
			this.adminData = data;
			this.agentData = data;

			this.totalSum += sumOfKeyInData({ data, keyToSum: 'DocTotalSum' });
		});
	}
	calculateTableHeight() {
		// const header = document.querySelector('.header');
		const infoRow = document.querySelector('.info-row');

		if (!infoRow) {
			return;
		}
		// const infoRowHeight = infoRow.clientHeight;
		const { height, top } = infoRow.getBoundingClientRect();
		const contentPadding = 35;
		const totalHeight = height + top + contentPadding;
		this.tableHeight = `calc(100vh - ${totalHeight}px)`;
	}

	onRowClick(payload: TeudotHistoryResponseWithClientId & { rowToScrollTo: number }) {
		this._dohotBizoimSales.rowToScrollTo = payload.rowToScrollTo;
		this.router.navigate([payload.DocCreateDate + '_' + payload.AgentId], { relativeTo: this.acr });
		// console.log(payload);
	}

	onScrolledToRow() {
		this._dohotBizoimSales.rowToScrollTo = 0;
	}
}
